import React, { useState } from 'react';
import {
  RegistrationMode, RegistrationState,
  RegistrationStartPrecision, registrationStartPrecision, formatRegistrationStart, MONTH_NAMES_DE,
  registrationStartDay, registrationStartTime,
} from '@radevents/shared';

/**
 * Jahre zur Auswahl: ab dem Vorjahr (die Anmeldung kann längst laufen) bis vier Jahre voraus.
 * Auswahl statt Zahlenfeld, weil ein halb getipptes „20" kein Datum ergibt.
 */
const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i);

/**
 * Die Zustände der Zeit-Auswahl — ein Regler statt mehrerer sich widersprechender Felder.
 * 'DAY' deckt auch den minutengenauen Fall ab: die Uhrzeit ist dort ein optionales Zusatzfeld
 * neben dem Datum, keine eigene Auswahl. Wer sie kennt, trägt sie ein; alle anderen ignorieren sie.
 */
type Timing = 'UNKNOWN' | 'OPEN' | 'CLOSED' | 'ONSITE' | Exclude<RegistrationStartPrecision, 'MINUTE'>;

/**
 * Beschriftung der einen Anmelde-Auswahl. „Nur vor Ort" steht hier mit drin statt in einem
 * eigenen Wahlfeld daneben: Es ist dieselbe Frage — wie steht es um die Anmeldung? — und zwei
 * Auswahlfelder fuer eine Frage liessen Widersprueche zu („nur vor Ort" plus Anmeldestart),
 * die spaeter niemand mehr aufloesen kann.
 */
const TIMING_LABELS: Record<Timing, string> = {
  UNKNOWN: 'Anmeldestart unbekannt',
  OPEN: 'Anmeldung ist bereits geöffnet',
  CLOSED: 'Anmeldung ist geschlossen',
  DAY: 'Öffnet am (Datum, optional mit Uhrzeit)',
  MONTH: 'Öffnet im (nur Monat)',
  YEAR: 'Öffnet in (nur Jahr)',
  ONSITE: 'Keine Online-Anmeldung — nur vor Ort',
};

const TIMING_ORDER: Timing[] = ['UNKNOWN', 'OPEN', 'CLOSED', 'DAY', 'MONTH', 'YEAR', 'ONSITE'];

/** Alles, was zur Anmeldung eines Events gehört. Der Link ist der `REGISTRATION`-Event-Link. */
export interface RegistrationValue {
  mode: RegistrationMode;
  /** Abgeschnittenes ISO-Datum ('2026-12-01', '2026-12', '2027') oder '' (kein Termin). */
  start: string;
  /** Gesetzter Stand („läuft bereits"/„geschlossen") — Alternative zum Termin, nie beides. */
  state: RegistrationState;
  /** Anmelde-URL; '' = keine hinterlegt. */
  url: string;
}

interface RegistrationFieldsProps {
  value: RegistrationValue;
  onChange: (next: RegistrationValue) => void;
  /** true = Redaktionssicht (Mod/Admin), false = öffentliches Vorschlagsformular. */
  internal: boolean;
  inputStyle: React.CSSProperties;
  /** Klassen der äußeren Zelle. Default passt in die zweispaltigen Event-Formulare. */
  className?: string;
  labelStyle?: React.CSSProperties;
}

/** Welche Zeit-Auswahl der Wert *aussagt*. null = der Wert ist leer und sagt nichts. */
const timingOf = (value: RegistrationValue): Timing | null => {
  if (value.mode === 'ONSITE_ONLY') return 'ONSITE';
  if (value.state !== 'UNKNOWN') return value.state;
  if (!value.start) return null;
  const precision = registrationStartPrecision(value.start);
  return precision === 'MINUTE' ? 'DAY' : precision;
};

/**
 * Der Anmelde-Block eines Events: **wie** man sich anmeldet, **ab wann** und **wo** (Link).
 *
 * Die drei Angaben hängen zusammen und stehen deshalb beieinander statt über das Formular
 * verstreut: „nur vor Ort" schließt Link und Termin aus, „online" verlangt den Link, und der
 * Termin ist oft nur grob bekannt („Dezember 2026") oder gar nicht — dann sagt man „läuft bereits".
 *
 * Der Zustand (Genauigkeit, angefangene Monatswahl) lebt hier und wird beim Aufbau aus [value]
 * abgeleitet; beide Formulare bauen sich beim Öffnen neu auf.
 */
const RegistrationFields: React.FC<RegistrationFieldsProps> = ({
  value, onChange, internal, inputStyle, className = 'sm:col-span-2', labelStyle,
}) => {
  /**
   * Die Auswahl wird aus [value] ABGELEITET und nicht eingefroren: die Formulare füllen sich
   * teils erst nach dem ersten Rendern (Dialog auf, Daten kommen nach). Ein einmalig gesetzter
   * useState-Startwert bliebe dann auf „unbekannt" stehen, obwohl am Event längst „läuft bereits"
   * steht. Der eigene State greift nur, solange der Wert leer ist und deshalb nichts aussagt —
   * dort muss die Wahl aber möglich sein, *bevor* es einen Wert gibt.
   */
  const [emptyChoice, setEmptyChoice] = useState<Timing>('UNKNOWN');
  const timing: Timing = timingOf(value) ?? emptyChoice;
  /**
   * Gewählter Monat, solange noch kein Jahr dasteht. „Dezember" allein ergibt kein Datum, ohne
   * diesen Zwischenspeicher fiele die Wahl aber wieder heraus, wenn man den Monat zuerst
   * anklickt — und genau das tut man bei „Dezember 2026".
   */
  const [monthDraft, setMonthDraft] = useState('');
  const day = registrationStartDay(value.start);
  const time = registrationStartTime(value.start);
  const month = day.slice(5, 7) || monthDraft;
  const year = day.slice(0, 4);
  const set = (patch: Partial<RegistrationValue>) => onChange({ ...value, ...patch });

  /** Jahr + Monat zum Teil-Datum. Ohne Monat bleibt das Jahr stehen — lieber grob als gar nichts. */
  const compose = (y: string, m: string) => (y ? (m ? `${y}-${m}` : y) : '');

  /**
   * Zeit-Auswahl umschalten und den Wert mitziehen: gröber wird abgeschnitten, feiner geleert —
   * aus „Dezember 2026" lässt sich kein Tag erraten, und ein geratener stünde als Tatsache da.
   */
  const changeTiming = (next: Timing) => {
    setEmptyChoice(next);
    if (next === 'MONTH') setMonthDraft(day.slice(5, 7));
    // Nur vor Ort heißt: kein Link und kein Online-Anmeldetermin. Beides stehen zu lassen wäre
    // ein Widerspruch, den später niemand mehr auflösen kann.
    if (next === 'ONSITE') return onChange({ mode: 'ONSITE_ONLY', start: '', state: 'UNKNOWN', url: '' });
    /**
     * Der Modus faellt aus derselben Wahl: Wer einen Anmeldestart nennt oder sagt, die Anmeldung
     * laufe bzw. sei zu, redet ueber eine Online-Anmeldung. Nur „unbekannt" sagt darueber nichts.
     * Fuer die oeffentliche Anzeige ist der Unterschied ohnehin keiner (`registrationStatusOf`
     * fragt nur nach ONSITE_ONLY) — er traegt allein den Hinweis „Link fehlt" in der
     * Anmeldestart-Uebersicht.
     */
    const mode: RegistrationMode = next === 'UNKNOWN' ? 'UNKNOWN' : 'ONLINE';
    // Ein gesetzter Stand und ein Termin schliessen sich aus — der Stand leert das Datum.
    if (next === 'OPEN' || next === 'CLOSED') return set({ mode, start: '', state: next });
    if (next === 'UNKNOWN') return set({ mode, start: '', state: 'UNKNOWN' });
    if (next === 'YEAR') return set({ mode, start: day.slice(0, 4), state: 'UNKNOWN' });
    if (next === 'MONTH') return set({ mode, start: day.slice(0, 7), state: 'UNKNOWN' });
    return set({ mode, start: day.length >= 10 ? value.start : '', state: 'UNKNOWN' });
  };

  const label = (text: string) => (
    <label className="block text-xs font-semibold mb-1" style={labelStyle ?? { color: 'var(--form-label)' }}>
      {text}
    </label>
  );

  const istVorOrt = timing === 'ONSITE';
  /**
   * Der Hinweis auf den fehlenden Link haengt an der *Aussage*, nicht mehr am weggefallenen
   * Wahlfeld: Wer sagt, die Anmeldung laufe oder oeffne an einem Termin, sollte auch sagen, wo.
   * Bei „unbekannt" waere die Mahnung Rauschen — die allermeisten Events haben keinen eigenen
   * Anmelde-Link, sondern nur die Veranstaltungs-Seite.
   */
  const missingLink = !istVorOrt && !value.url.trim()
    && (timing === 'OPEN' || timing === 'DAY' || timing === 'MONTH' || timing === 'YEAR');

  return (
    <div className={className}>
      {label('Anmeldung')}
      <div className="flex flex-col gap-2">
        {/* Eine Auswahl fuer die ganze Frage: Stand, Termin und der Fall „nur vor Ort". */}
        <div className="flex flex-wrap items-center gap-2">
          <select value={timing} onChange={e => changeTiming(e.target.value as Timing)}
            aria-label="Stand der Anmeldung"
            className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            {TIMING_ORDER.map(t => (
              <option key={t} value={t}>{TIMING_LABELS[t]}</option>
            ))}
          </select>

          {timing === 'DAY' && (
            <>
              <input type="date" value={day.length >= 10 ? day : ''}
                onChange={e => set({ start: e.target.value ? `${e.target.value}${time ? `T${time}` : ''}` : '', state: 'UNKNOWN' })}
                aria-label="Datum des Anmeldestarts"
                className="px-3 py-2 rounded-lg text-sm" style={inputStyle} />
              {/* Uhrzeit optional: bei begehrten Veranstaltungen entscheiden Minuten, bei allen
                  anderen weiß sie niemand. Ohne Datum ergibt sie keinen Sinn. */}
              <input type="time" value={time} disabled={day.length < 10}
                onChange={e => set({ start: e.target.value ? `${day}T${e.target.value}` : day, state: 'UNKNOWN' })}
                aria-label="Uhrzeit des Anmeldestarts"
                className="px-3 py-2 rounded-lg text-sm"
                style={{ ...inputStyle, opacity: day.length >= 10 ? 1 : 0.55 }} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Uhrzeit optional</span>
            </>
          )}
          {timing === 'MONTH' && (
            <select value={month} aria-label="Monat des Anmeldestarts"
              onChange={e => { setMonthDraft(e.target.value); set({ start: compose(year, e.target.value), state: 'UNKNOWN' }); }}
              className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">— Monat —</option>
              {MONTH_NAMES_DE.map((name, i) => (
                <option key={name} value={String(i + 1).padStart(2, '0')}>{name}</option>
              ))}
            </select>
          )}
          {(timing === 'MONTH' || timing === 'YEAR') && (
            <select value={year} aria-label="Jahr des Anmeldestarts"
              onChange={e => set({ start: compose(e.target.value, timing === 'MONTH' ? month : ''), state: 'UNKNOWN' })}
              className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">— Jahr —</option>
              {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          )}
          {value.start && (
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              = {formatRegistrationStart(value.start)}
            </span>
          )}
        </div>

        {/* Wo man sich anmeldet. Es ist derselbe REGISTRATION-Link, den auch der Link-Editor
            fuehrt — hier steht er, weil er zur Anmeldung gehoert und nicht zu „weitere Links". */}
        {!istVorOrt && (
          <input type="url" value={value.url} onChange={e => set({ url: e.target.value })}
            aria-label="Anmelde-Link" placeholder="Anmelde-Link (https://…)"
            className="px-3 py-2 rounded-lg text-sm w-full" style={inputStyle} />
        )}

        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {value.state === 'CLOSED'
            ? 'Die Anmeldung ist zu — auf der Event-Seite steht „Anmeldung geschlossen". Der Link bleibt als „Zur Anmelde-Seite" stehen: dort steht meist, ob vor Ort noch etwas geht.'
            : istVorOrt
            ? 'Anmeldung nur am Veranstaltungstag vor Ort — Link und Termin entfallen.'
            : internal
              ? 'Der Anmelde-Link steht öffentlich auf der Event-Seite, ebenso der Hinweis „Anmeldung geöffnet". Das genaue Startdatum bleibt intern.'
              : 'Wann öffnet die Anmeldung? Nur den Monat zu wissen reicht. Das genaue Datum zeigen wir nicht öffentlich an.'}
          {missingLink && ' Ohne Link können wir nur „Anmeldung geöffnet" anzeigen, aber nicht dorthin verlinken.'}
        </p>
      </div>
    </div>
  );
};

export default RegistrationFields;
